import { RoundingMode } from "@/models/time-control/rounding";
import { datetime_round_to } from "@/time";
import type { DateTime } from "luxon";

/**
 * Calculates the auto-refresh display time based on the current time and a specified refresh rate.
 *
 * The function performs the following steps:
 * 1. Validates the refresh rate to ensure it is a positive number.
 * 2. Subtracts the refresh rate (in minutes) from the current time to determine the initial display time.
 * 3. Rounds the resulting time down to the nearest increment specified by the refresh rate.
 *
 * This approach ensures that the displayed time reflects the most recent data available,
 * accounting for potential delays in updates that may correspond to the specified refresh rate.
 *
 * @param currTime - The current date and time as a DateTime object.
 * @param refreshRate - The refresh rate in minutes, indicating the desired interval for rounding.
 * @returns The adjusted DateTime object representing the auto-refresh display time.
 * @throws Error if the refresh rate is less than or equal to zero.
 */
export const getAutoRefreshTime = (currTime: DateTime, refreshRate: number) => {
  // Check for invalid refresh rate
  if (refreshRate <= 0) {
    throw new Error("Invalid refresh rate: must be a positive number");
  }

  const roundedDisplayTime = datetime_round_to(
    currTime.minus({ minutes: refreshRate }),
    "minute",
    refreshRate,
    RoundingMode.Floor,
  );
  return roundedDisplayTime;
};

export function roundTimeToInterval(date: DateTime, interval: number): DateTime {
  if (!Number.isFinite(interval) || interval <= 0) {
    throw new Error("interval must be a positive number");
  }
  const roundedMinutes = Math.floor(date.minute / interval) * interval;
  return date.set({ minute: roundedMinutes, second: 0, millisecond: 0 });
}
